import { useSelector, useDispatch } from "react-redux";
import {
  master,
  useGetImageCertificationListDataQuery,
  useUpdateImageCertificationTableHeadDataMutation,
} from "../../../store/queries/Master";
import { useGetFilterListDataQuery } from "../../../store/queries/Verification";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { updateConfig } from "../../../store/slices/ImageCertification/imageCertificationSlice";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { downloadCertificate, downloadKalyanExcel } from "../api";
import moment from "moment";
import { subDays } from "date-fns";
import getFilterCount from "../../../utils/getFilterCount";

const useImageCertification = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "40 Per page", value: 40 },
  ];

  const dispatch = useDispatch();

  const [date, setDate] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    key: "selection",
  });

  const [excelDownloadingIds, setExcelDownloadingIds] = useState(new Set());
  const [certDownloadingIds, setCertDownloadingIds] = useState(new Set());

  const { tableFields, showEditModal } = useSelector((state) => state.global);
  const imageCertificationState = useSelector(
    (state) => state.imageCertification,
  );

  const {
    data: imageCertificationList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetImageCertificationListDataQuery({
    page_size: imageCertificationState.currentPageSize,
    page: imageCertificationState.currentPage,
    sort_by: imageCertificationState.sortBy,
    sort_order: imageCertificationState.sortOrder,
    search: imageCertificationState.search,
    from_date: imageCertificationState.date?.[0],
    to_date: imageCertificationState.date?.[1],
    item_type: imageCertificationState?.selectedItemType || [],
    certificate_number: imageCertificationState?.certificateNumber,
  });

  const { data: basicData = {} } = useGetFilterListDataQuery({});

  const [updateImageCertificationFields] =
    useUpdateImageCertificationTableHeadDataMutation();

  useEffect(() => {
    dispatch(
      globalUpdateConfig((state) => {
        state.tableFields = imageCertificationList?.data?.fields;
      }),
    );
    // eslint-disable-next-line
  }, [imageCertificationList]);

  const tableFieldsWithAction = useMemo(() => {
    return {
      ...tableFields,
      action: { label: "Action", key: "action" },
    };
  }, [tableFields]);

  useEffect(() => {
    if (imageCertificationState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        }),
      );
    }
    // eslint-disable-next-line
  }, [imageCertificationState.clearSelection]);

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      }),
    );
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      }),
    );
  };

  const handleDateChange = (date) => {
    const startDateUTC = new Date(date.startDate);
    const endDateUTC = new Date(date.endDate);
    const timeZoneOffset = 5.5 * 60 * 60 * 1000;
    const startDateIST = new Date(startDateUTC.getTime() + timeZoneOffset);
    const endDateIST = new Date(endDateUTC.getTime() + timeZoneOffset);
    const formatDate = (date) => {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    const startDateFormatted = formatDate(startDateIST);
    const endDateFormatted = formatDate(endDateIST);
    dispatch(
      updateConfig(
        (state) => (state.date = [startDateFormatted, endDateFormatted]),
      ),
    );
  };

  const handleClearClick = () => {
    dispatch(
      updateConfig((state) => {
        state.search = "";
        state.selectedItemType = [];
        state.date = [
          new Date(subDays(new Date(), 30)).toISOString().split("T")[0],
          new Date().toISOString().split("T")[0],
        ];
        state.certificateNumber = "";
        state.tempCertificateNumber = "";
        state.selectedItemType = [];
        state.tempSelectedItemType = [];
      }),
    );
  };

  const handleCertificateNumberChange = (e) => {
    dispatch(
      updateConfig((state) => {
        state.tempCertificateNumber = e.target.value;
      }),
    );
  };

  const handleCertificateNumberSubmit = () => {
    dispatch(
      updateConfig((state) => {
        state.certificateNumber = state.tempCertificateNumber;
        state.selectedItemType = state.tempSelectedItemType;
        state.currentPage = 1;
        state.showFilter = false;
      }),
    );
  };

  const handleItemTypeChange = (selectedItemTypes, actionMeta) => {
    dispatch(
      updateConfig((state) => {
        state.tempSelectedItemType = selectedItemTypes
          ? selectedItemTypes.map((item) => item._id)
          : [];
      }),
    );
  };

  const handleFilter = () => {
    dispatch(
      updateConfig((state) => {
        state.showFilter = true;
        state.tempCertificateNumber = state.certificateNumber;
        state.tempSelectedItemType = state.selectedItemType;
      }),
    );
  };

  const handleFilterClose = () => {
    dispatch(
      updateConfig((state) => {
        state.showFilter = false;
      }),
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      }),
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      }),
    );
  };

  const handleExcelDownload = (data) => {
    if (excelDownloadingIds.has(data?._id)) return;
    const body = {
      order_type: data?.order_type,
      receipt_id: data?.receipt_id,
      gli_number: data?.gli_number,
    };
    setExcelDownloadingIds((prev) => new Set(prev).add(data?._id));
    downloadKalyanExcel(body)
      .then((response) => {
        if (response?.status === 200) {
          let dataType = response?.data?.type;
          let binaryData = [];
          binaryData.push(response?.data);
          let downloadLink = document.createElement("a");
          downloadLink.href = window.URL.createObjectURL(
            new Blob(binaryData, { type: dataType }),
          );
          downloadLink.setAttribute(
            "download",
            `${data?.gli_number}_${moment(Date.now()).format("MM_DD_YYYY_HH_MM_SS")}_Excel.xlsx`,
          );
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          toast.success("Downloaded successfully");
        } else {
          toast.error("Something went wrong");
        }
        setExcelDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(data?._id);
          return next;
        });
      })
      .catch(() => {
        setExcelDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(data?._id);
          return next;
        });
      });
  };

  const handleDownloadClick = (checked) => {
    let gliData = imageCertificationList?.data?.data?.find(
      (item) => item?._id === checked?.[0],
    );
    const id = checked?.[0];
    if (!id || certDownloadingIds.has(id)) return;
    setCertDownloadingIds((prev) => new Set(prev).add(id));
    downloadCertificate(id)
      .then((response) => {
        if (response?.status === 200) {
          let dataType = response?.data?.type;
          let binaryData = [];
          binaryData.push(response?.data);
          let downloadLink = document.createElement("a");
          downloadLink.href = window.URL.createObjectURL(
            new Blob(binaryData, { type: dataType }),
          );
          downloadLink.setAttribute("download", `${gliData?.gli_number}.zip`);
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          toast.success("Downloaded successfully");
        } else {
          toast.error("Something went wrong");
        }
        setCertDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      })
      .catch(() => {
        setCertDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      });
  };

  const orderTypeLabels = {
    1: "Fresh",
    2: "Recheck",
    3: "Reissue",
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      order_type: (field, data) => (
        <p className="pro-mb-0">
          {orderTypeLabels[data[field]] ?? data[field]}
        </p>
      ),
      action: (field, data) => (
        <div className="pro-d-flex pro-items-center pro-gap-2">
          <button
            className="pro-btn pro-btn-outline pro-btn-sm pro-items-center"
            onClick={() => handleExcelDownload(data)}
            title="Download Excel"
            disabled={excelDownloadingIds.has(data?._id)}
          >
            {excelDownloadingIds.has(data?._id) ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
              ></span>
            ) : (
              <span className="material-symbols-outlined">table_view</span>
            )}
          </button>
          <button
            className="pro-btn pro-btn-primary pro-btn-sm pro-items-center"
            onClick={() => handleDownloadClick([data?._id])}
            title="Download Certificate"
            disabled={certDownloadingIds.has(data?._id)}
          >
            {certDownloadingIds.has(data?._id) ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
              ></span>
            ) : (
              <span className="material-symbols-outlined">download</span>
            )}
          </button>
        </div>
      ),
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handleSort = (label) => {
    if (imageCertificationState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder =
            imageCertificationState.sortOrder === "asc" ? "desc" : "asc";
        }),
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        }),
      );
    }
  };

  const filterCount = useMemo(() => {
    return getFilterCount({
      item_type: imageCertificationState?.selectedItemType,
      certificate_number: imageCertificationState?.certificateNumber,
    });
  }, [
    imageCertificationState?.selectedItemType,
    imageCertificationState?.certificateNumber,
  ]);

  return {
    date,
    imageCertificationState,
    showEditModal,
    tableFields: tableFieldsWithAction,
    imageCertificationList,
    isLoading,
    isFetching,
    paginationOptions,
    refetch,
    getRow,
    handleDateChange,
    handleEditColumnsClick,
    handleSort,
    handlePagination,
    handleSearch,
    updateImageCertificationFields,
    handleClearClick,
    handlePageSize,
    handleDownloadClick,
    handleItemTypeChange,
    handleCertificateNumberChange,
    handleCertificateNumberSubmit,
    handleFilter,
    handleFilterClose,
    filterCount,
    itemTypeList: basicData,
  };
};

export default useImageCertification;
